/**
 * 投稿編集画面の「参考ファイル」添付。
 * 仕様書: docs/02_work/done/plan_board_feature.md §7-5
 *
 * 作業の際に参照してほしい資料を投稿者が添付する。詳細画面の「成果物」添付
 * (BoardAttachmentSection) と同じ Storage / Callable を共有し、kind='reference'
 * で区別する。
 *
 * ★ 新規作成時は投稿ドキュメントがまだ存在せず、registerBoardAttachment が
 *   loadPost() で not-found になる。そのため **postExists=false のあいだは
 *   アップロードせずファイルを保持** し、投稿の保存が成功した後に親
 *   (BoardPostEditorPage) がまとめてアップロード + 登録する。
 *   保持中のファイルは Storage に置かれないので孤児も発生しない。
 */
import type { BoardAttachmentView } from '@goenhub/shared';
import { Download, FileUp, Paperclip, Trash2, X } from 'lucide-react';
import { useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/input';
import { toast } from '@/components/ui/toast';
import {
  formatBytes,
  startDownload,
  useBoardAttachments,
  useDeleteBoardAttachment,
  useRegisterBoardAttachment,
  useResolveAttachmentDownload,
} from '@/hooks/useBoardAttachments';
import { uploadBoardAttachment } from '@/lib/boardMedia';

const MAX_MB = 100;

interface Props {
  postId: string;
  myUid: string | null;
  /** 投稿が既に保存済みか。false のあいだはアップロードせず保持する。 */
  postExists: boolean;
  /** 保存前に保持しているファイル（親が保持し、保存成功後にアップロードする）。 */
  pendingFiles: File[];
  onPendingChange: (files: File[]) => void;
}

export function BoardReferenceAttachments({
  postId,
  myUid,
  postExists,
  pendingFiles,
  onPendingChange,
}: Props) {
  // 未保存の投稿には一覧クエリを打たない（必ず NOT_FOUND になるため）
  const list = useBoardAttachments(postExists ? postId : undefined);
  const register = useRegisterBoardAttachment(postId);
  const removeAttachment = useDeleteBoardAttachment(postId);
  const resolveDownload = useResolveAttachmentDownload(postId);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [progress, setProgress] = useState<number | null>(null);

  const attachments = (list.data ?? []).filter((a) => a.kind === 'reference');
  const busy = progress !== null || register.isPending;

  const handleFile = async (f: File | null | undefined) => {
    if (!f || !myUid) return;
    if (f.size === 0) {
      toast.error('空のファイルはアップロードできません');
      return;
    }
    if (f.size > MAX_MB * 1024 * 1024) {
      toast.error(`ファイルサイズは ${MAX_MB}MB 以下にしてください`);
      return;
    }
    // 未保存の投稿にはまだ添付できない（サーバが投稿の存在を要求する）。
    // 保存が成功した後に親がまとめてアップロードする。
    if (!postExists) {
      onPendingChange([...pendingFiles, f]);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    try {
      setProgress(0);
      const storagePath = await uploadBoardAttachment(postId, myUid, f, setProgress);
      await register.mutateAsync({
        storagePath,
        fileName: f.name,
        size: f.size,
        contentType: f.type,
        kind: 'reference',
      });
      toast.success('参考ファイルを添付しました');
    } catch (err) {
      toast.error(
        `アップロードに失敗しました: ${err instanceof Error ? err.message : String(err)}`,
      );
    } finally {
      setProgress(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDownload = async (a: BoardAttachmentView) => {
    try {
      const res = await resolveDownload.mutateAsync({ attachmentId: a.id });
      if (res.status === 'ok') startDownload(res.downloadUrl);
      else toast.error('ダウンロードできませんでした');
    } catch (err) {
      toast.error(`ダウンロード失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const handleDelete = async (a: BoardAttachmentView) => {
    if (!window.confirm(`「${a.fileName}」を削除します。よろしいですか？`)) return;
    try {
      await removeAttachment.mutateAsync(a.id);
      toast.success('削除しました');
    } catch (err) {
      toast.error(`削除失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-2">
        <Paperclip className="size-4 text-[var(--text-secondary)]" />
        <Label>
          参考ファイル
          <span className="text-[var(--text-muted)]">（任意・作業時に参照する資料）</span>
        </Label>
      </div>

      {attachments.length > 0 && (
        <div className="space-y-1.5 mt-1.5">
          {attachments.map((a) => (
            <div
              key={a.id}
              className="rounded-md border border-[var(--border)] p-2 flex items-center gap-2"
            >
              <div className="min-w-0 flex-1">
                <div className="text-sm text-[var(--text-primary)] truncate">{a.fileName}</div>
                <div className="text-[11px] text-[var(--text-muted)]">
                  {formatBytes(a.size)} ・ {a.uploaderName}
                </div>
              </div>
              <Button
                type="button"
                variant="secondary"
                onClick={() => void handleDownload(a)}
                disabled={resolveDownload.isPending}
              >
                <Download className="size-4" />
              </Button>
              {a.uploaderUid === myUid && (
                <Button
                  type="button"
                  variant="danger"
                  onClick={() => void handleDelete(a)}
                  disabled={removeAttachment.isPending}
                >
                  <Trash2 className="size-4" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* 保存前に保持しているファイル（保存時にアップロードする） */}
      {pendingFiles.length > 0 && (
        <div className="space-y-1.5 mt-1.5">
          {pendingFiles.map((f, i) => (
            <div
              key={`${f.name}-${i}`}
              className="rounded-md border border-dashed border-[var(--border)] p-2 flex items-center gap-2"
            >
              <div className="min-w-0 flex-1">
                <div className="text-sm text-[var(--text-primary)] truncate">{f.name}</div>
                <div className="text-[11px] text-[var(--text-muted)]">
                  {formatBytes(f.size)} ・ 保存時にアップロード
                </div>
              </div>
              <button
                type="button"
                onClick={() => onPendingChange(pendingFiles.filter((_, j) => j !== i))}
                aria-label={`${f.name} を外す`}
                className="text-[var(--text-muted)] hover:text-[var(--text-primary)]"
              >
                <X className="size-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        onChange={(e) => void handleFile(e.target.files?.[0])}
      />

      {progress !== null ? (
        <div className="mt-1.5 rounded-md border border-[var(--border)] p-2">
          <div className="h-1.5 rounded-full bg-[var(--border)] overflow-hidden">
            <div
              className="h-full bg-[var(--accent)] transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="mt-1 text-[11px] text-[var(--text-muted)] text-right">{progress}%</div>
        </div>
      ) : (
        <div
          role="button"
          tabIndex={0}
          onClick={() => !busy && fileInputRef.current?.click()}
          onKeyDown={(e) => {
            if (!busy && (e.key === 'Enter' || e.key === ' ')) fileInputRef.current?.click();
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            if (!busy) void handleFile(e.dataTransfer.files?.[0]);
          }}
          className={`mt-1.5 rounded-md border border-dashed p-3 text-center cursor-pointer transition-colors ${
            dragOver ? 'border-[var(--accent)]' : 'border-[var(--border)]'
          }`}
        >
          <FileUp className="size-4 mx-auto mb-1 text-[var(--text-muted)]" />
          <div className="text-xs text-[var(--text-secondary)]">
            ファイルをドラッグ＆ドロップ
          </div>
          <div className="text-[11px] text-[var(--text-muted)]">
            クリックで選択／全形式・{MAX_MB}MBまで
          </div>
        </div>
      )}
    </div>
  );
}
